import { useSyncExternalStore } from 'react';

/**
 * Whether Cmd (macOS) or Ctrl is currently held down. Drives the IDE-style
 * "modifier turns this into a link" affordance on reference fields and cells.
 *
 * One window subscription shared by every consumer: a grid renders one
 * reference cell per row, and per-hook listeners would register hundreds of
 * keydown/keyup handlers for a single boolean.
 *
 * Reset on window blur: Cmd+Tab away releases the key outside the page, so
 * without it the link styling would stay stuck until the next keyup.
 */
let held = false;
const subscribers = new Set<() => void>();

function publish(next: boolean) {
  if (next === held) return;
  held = next;
  subscribers.forEach((notify) => notify());
}

function onKey(event: KeyboardEvent) {
  publish(event.metaKey || event.ctrlKey);
}

function onBlur() {
  publish(false);
}

function subscribe(notify: () => void): () => void {
  if (subscribers.size === 0) {
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', onKey);
    window.addEventListener('blur', onBlur);
  }
  subscribers.add(notify);
  return () => {
    subscribers.delete(notify);
    if (subscribers.size === 0) {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('keyup', onKey);
      window.removeEventListener('blur', onBlur);
      held = false;
    }
  };
}

const getSnapshot = () => held;
const getServerSnapshot = () => false;

export function useModifierKeyHeld(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
